import React, {ChangeEvent, FormEvent, useRef, useState} from 'react'
import {KTSVG, toAbsoluteUrl} from '../../../../../_metronic/helpers'
import {useSenderIdProvider} from '../../context/SenderIdProvider'
import {useAuth} from '../../../auth'
import {Badge, Button, Form} from 'react-bootstrap'
import {handleSampleDownload} from '../../../../utils/helperFunction/downloadHelper'
import {downloadSenderSample, statusOptions, UploadDetailsResponse} from '../_model'
import * as XLSX from 'xlsx'
import axios from 'axios'
import {BASE_URL} from '../../../config'
import toast from 'react-hot-toast'
import {useUserStateProvider} from '../../../../utils/contextProvider/UserStateProvider'

const UPLOAD_BULK_SENDER_FILE_URL = `${BASE_URL}/api/protect/senderFileupload`
const UPLOAD_BULK_SENDER_FILE_URL2 = `${BASE_URL}/api/protect/uploadSenderAndSave`
const ALLOWED_FILE_EXTENSIONS = ['csv', 'xl', 'xls', 'xlsx']
const INVALID_FILE_MESSAGE = 'Please select a valid CSV or Excel file (.csv, .xls, .xlsx).'

const getFirstColumnHeader = async (file: File): Promise<string | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        let workbook: XLSX.WorkBook

        if (file.type === 'text/csv') {
          // Read CSV as a string
          const csvData = e.target?.result as string
          workbook = XLSX.read(csvData, {type: 'string'})
        } else {
          // Read Excel files as binary
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          workbook = XLSX.read(data, {type: 'array'})
        }

        // Ensure there is at least one sheet
        if (workbook.SheetNames.length === 0) {
          return resolve(null)
        }

        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]

        // Convert sheet to JSON (array of arrays)
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, {header: 1}) as (
          | string
          | undefined
        )[][]

        // Extract first column header (A1)
        const firstColumnHeader = jsonData[0]?.[0] || null
        resolve(firstColumnHeader)
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = (error) => reject(error)

    if (file.type === 'text/csv') {
      reader.readAsText(file) // Read CSV as text
    } else {
      reader.readAsArrayBuffer(file) // Read Excel files as binary
    }
  })
}

const isValidBulkSenderFile = (file: File) => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase()

  return !!fileExtension && ALLOWED_FILE_EXTENSIONS.includes(fileExtension)
}

const BulkSenderCreate = () => {
  const {auth} = useAuth()
  const {options, hide, setUploadResponse, getSenderIds} = useSenderIdProvider()
  const {isGlobalP2p} = useUserStateProvider()

  const [selectRoute, setSelectRoute] = useState<string | undefined>('')
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)
  const [senderStatus, setSenderStatus] = useState('')
  const [fileError, setFileError] = useState('')
  const [senderType, setSenderType] = useState('0')

  const inputFile = useRef<HTMLInputElement>(null)

  const resetForm = () => {
    setSelectRoute('')
    setSelectedFile(undefined)
    setSenderStatus('')
    setFileError('')

    if (inputFile.current) {
      inputFile.current.value = ''
    }
  }

  const handleCancel = () => {
    resetForm()
    hide()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (!file) {
      setSelectedFile(undefined)
      setFileError('')
      return
    }

    if (!isValidBulkSenderFile(file)) {
      setSelectedFile(undefined)
      setFileError(INVALID_FILE_MESSAGE)
      event.target.value = ''
      toast.error(INVALID_FILE_MESSAGE)
      return
    }

    setFileError('')
    setSelectedFile(file)
  }

  const onDownloadSenderSample = () => handleSampleDownload(downloadSenderSample, 'sender')

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    // console.log(selectRoute, selectedFile)
    if (selectedFile && selectRoute) {
      if (!isValidBulkSenderFile(selectedFile)) {
        setSelectedFile(undefined)
        setFileError(INVALID_FILE_MESSAGE)

        if (inputFile.current) {
          inputFile.current.value = ''
        }

        toast.error(INVALID_FILE_MESSAGE)
        return
      }

      const header = await getFirstColumnHeader(selectedFile)
      const statusId = senderStatus.toLowerCase() === 'active' ? '1' : '0'
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('routeId', selectRoute)
      formData.append('status', statusId)

      if (isGlobalP2p && senderType) formData.append('isP2p', senderType)

      try {
        // const response = await axios.post<UploadDetailsResponse>(
        //   `${UPLOAD_BULK_SENDER_FILE_URL}/${header}`,
        //   formData,
        //   {
        //     headers: {
        //       Authorization: `Bearer ${auth?.accessToken}`,
        //       'Content-Type': 'multipart/form-data',
        //     },
        //   }
        // )

        const response = await axios.post<UploadDetailsResponse>(
          `${UPLOAD_BULK_SENDER_FILE_URL2}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${auth?.accessToken}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        )
        const data = response.data
        setUploadResponse(data)
        resetForm()
        hide()
        getSenderIds()
      } catch (error) {
        console.log(error)
      }
    } else {
      toast.error('Please input required values')
    }
  }

  return (
    <div className='modal fade' tabIndex={-2} id='kt_modal_2'>
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content py-2'>
          <div className='modal-header border-0'>
            <h4 className='modal-title fs-1'>Sender ID's</h4>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              data-bs-dismiss='modal'
              aria-label='Close'
              onClick={handleCancel}
            >
              <KTSVG
                path='/media/icons/duotune/arrows/arr061.svg'
                className='svg-icon svg-icon-2x'
              />
            </div>
          </div>
          <div className='modal-body px-10 pt-0'>
            <form onSubmit={handleSubmit}>
              <label className='col-lg-4 col-form-label fs-4'>
                <span className='required fs-4'>Route</span>
              </label>
              <Form.Select
                value={selectRoute}
                onChange={(e) => setSelectRoute(e.target.value)}
                placeholder='Select a route'
                className='custom-input'
                required
              >
                <option value=''>Select a route</option>
                {options &&
                  options.map((option: any) => {
                    return (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    )
                  })}
              </Form.Select>
              <span className='fs-6'>Which route will be used to send the message</span>

              <div className='mb-4'>
                <label className='col-lg-4 col-form-label fs-4'>
                  <span className='required'>Status</span>
                </label>

                <div className='col-lg-12 fv-row'>
                  <Form.Select
                    value={senderStatus}
                    onChange={(e) => {
                      e.persist()
                      setSenderStatus(e.target.value)
                    }}
                    placeholder='Select status'
                    className='custom-input'
                  >
                    <option value=''>Select status</option>
                    {statusOptions &&
                      statusOptions.map((option) => {
                        return (
                          <option key={option.id} value={option.value}>
                            {option.label}
                          </option>
                        )
                      })}
                  </Form.Select>
                </div>
              </div>

              {isGlobalP2p && (
                <div className='mb-4'>
                  <label className='col-lg-4 col-form-label  fs-6'>
                    <span className='required fs-4'>Sender Type</span>
                  </label>
                  <Form.Select
                    value={senderType}
                    onChange={(e) => setSenderType(e.target.value)}
                    placeholder='Select type'
                    className='custom-input'
                    required
                  >
                    <option value=''>Select type</option>
                    <option value='2'>P2P</option>
                    <option value='0'>A2P</option>
                  </Form.Select>
                </div>
              )}

              <div className=''>
                <label className='required col-form-label fs-4'>File</label>
                <input
                  className='form-control custom-input'
                  ref={inputFile}
                  type='file'
                  style={{display: 'block'}}
                  onChange={handleFileChange}
                  accept='.csv,.xl,.xls,.xlsx'
                />
                {fileError && <div className='text-danger fs-7 mt-2'>{fileError}</div>}
              </div>

              <div className='mt-8'>
                <span className='fs-6'>Sample File to Upload Sender ID's </span>
                <Badge bg='secondary' style={{cursor: 'pointer'}} onClick={onDownloadSenderSample}>
                  Download sample file
                </Badge>

                <img
                  alt='Sample View'
                  className='mt-6'
                  src={toAbsoluteUrl('/media/stock/sender_sample.png')}
                />
              </div>

              <div className='text-center pt-15'>
                <button
                  type='reset'
                  className='btn btn-light me-3 border border-gray-600'
                  data-kt-users-modal-action='cancel'
                  data-bs-dismiss='modal'
                  aria-label='Close'
                  onClick={handleCancel}
                >
                  Close
                </button>
                <button
                  type='submit'
                  className='btn btn-primary me-3'
                  data-bs-dismiss='modal'
                  aria-label='Close'
                  data-bs-toggle='modal'
                  data-bs-target='#kt_modal_10'
                >
                  Create Sender
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BulkSenderCreate
